import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';

import {
  getActiveCompanyContactPersons,
  getActiveCompany } from '~selectors'; // eslint-disable-line
import {
  fetchContactPersons,
  addContactPerson,
  updateContactPerson } from '~actions'; // eslint-disable-line

import messages from './messages/ContactPersonManager_messages';

import ActionLog from '../actionlog/actionlog.container';
import ContactPersonForm from './ContactPersonForm';
import ContactPersonInfo from './ContactPersonInfo';
import ContactPersonSelector from './ContactPersonSelector';
import './styles/ContactPersonManager.scss';

import Button from '../../components/Button';

const propTypes = {
  actionLog: PropTypes.array,
  addContactPerson: PropTypes.func.isRequired,
  addError: PropTypes.bool.isRequired,
  addInProgress: PropTypes.bool.isRequired,
  children: PropTypes.func,
  company: PropTypes.object.isRequired,
  contactPersons: PropTypes.array,
  externalControls: PropTypes.bool,
  fetchContactPersons: PropTypes.func.isRequired,
  latestAction: PropTypes.number,
  onSelectContactPerson: PropTypes.func,
  initialContactPerson: PropTypes.object,
  updateContactPerson: PropTypes.func.isRequired,
  updateError: PropTypes.bool.isRequired,
  updateInProgress: PropTypes.bool.isRequired,
};

const defaultProps = {
  children: () => {},
  company: {},
  contactPersons: [],
  externalControls: false,
  initialContactPerson: null,
  onSelectContactPerson: () => {},
};

class ContactPersonManager extends Component {
  static STATE_ADD = 'add'
  static STATE_DETAILS = 'details'
  static STATE_UPDATE = 'update'

  constructor(props) {
    super(props);

    this.initialState = {
      state: ContactPersonManager.STATE_DETAILS,
      selected: props.initialContactPerson,
    };
    this.state = this.initialState;
  }

  componentDidMount = () => {
    const {
      company: { country, identifier, id: companyId },
      contactPersons } = this.props;
    if (contactPersons === null) {
      // Fetch contact persons for the company if they're not in store.
      this.props.fetchContactPersons({ country, identifier, companyId });
    }
  }

  componentDidUpdate = prevProps => {
    // latestAction is Date.now() -timestamp when something happened with
    // contact persons. Done in reducer.
    if (prevProps.latestAction !== this.props.latestAction) {
      // The same timestamp is also then tagged to either added or updated
      // contact person. Done in reducer.
      const selected = this.props.contactPersons.find(
        cp => cp.timestamp === this.props.latestAction
      );
      if (selected) {
        const newState = { state: this.state.state, selected };
        if (prevProps.updateInProgress && !this.props.updateInProgress) {
          // Latest finished action was an update.
          if (!this.props.updateError) {
            // Update was a success. set state to details.
            newState.state = ContactPersonManager.STATE_DETAILS;
          }
        } else if (prevProps.addInProgress && !this.props.addInProgress) {
          // Latest finished action was an add.
          if (!this.props.addError) {
            newState.state = ContactPersonManager.STATE_DETAILS;
          }
        }
        // Finally set local state and trigger onSelectContactPerson-hook.
        this.setState(newState, () =>
          this.props.onSelectContactPerson({ contactPerson: selected })
        );
      }
    }
  }

  onSelectContactPerson = ({ contactPerson }) => {
    this.setState({
      ...this.initialState,
      selected: contactPerson
    }, () => this.props.onSelectContactPerson({ contactPerson }));
  }

  onAdd = ({ contactPerson }) => {
    const { company: { country, identifier, id: companyId }} = this.props;
    this.setState({ selected: null }, () => {
      this.props.addContactPerson({
        identifier,
        contactPerson,
        country,
        companyId,
      });
    });
  }

  onUpdate = ({ contactPerson }) => {
    const {
      contactPersons,
      company: {
        country,
        identifier,
        id: companyId,
      },
    } = this.props;
    const oldContactPerson = contactPersons.find(
      cp => cp.id === contactPerson.id
    );
    this.props.updateContactPerson({
      contactPerson,
      oldContactPerson,
      country,
      identifier,
      companyId,
    });
  }

  add = evnt => {
    evnt.preventDefault();
    this.setState({ state: ContactPersonManager.STATE_ADD });
  }

  cancelAdd = evnt => {
    if (evnt && evnt.preventDefault) {
      evnt.preventDefault();
    }
    this.setState({ state: ContactPersonManager.STATE_DETAILS });
  }

  update = evnt => {
    evnt.preventDefault();
    this.setState({ state: ContactPersonManager.STATE_UPDATE });
  }

  cancelUpdate = evnt => {
    if (evnt && evnt.preventDefault) {
      evnt.preventDefault();
    }
    this.setState({ state: ContactPersonManager.STATE_DETAILS });
  }

  get shouldShowForm() {
    return this.state.state !== ContactPersonManager.STATE_DETAILS;
  }

  get shouldShowInfo() {
    const { selected } = this.state;
    return selected !== null && !this.shouldShowForm;
  }

  renderSelector = () => {
    let selector = (
      <ContactPersonSelector
        disabled={this.shouldShowForm}
        onChange={this.onSelectContactPerson}
        options={this.props.contactPersons}
        selected={this.state.selected}
      />
    );
    let addButton = (
      <Button
        disabled={this.shouldShowForm}
        id="contact-person-add-button"
        onClick={this.add}
      >
        <FormattedMessage {...messages.addButtonLabel} />
      </Button>
    );

    if (this.props.contactPersons === null) {
      // Contact persons not fetched yet.
      addButton = null;
      selector = (
        <FormattedMessage {...messages.fetchingInfo} />
      );
    } else if (!this.props.contactPersons.length) {
      // Company has no contact persons
      selector = (
        <FormattedMessage {...messages.notFoundInfo} />
      );
    }

    return (
      <div styleName='input-group selector'>
        {selector}
        <div styleName='padder' />
        {addButton}
      </div>
    );
  };

  render = () =>
    <div styleName='ContactPersonManager'>
      <ActionLog actionLog={this.props.actionLog} />
      {this.renderSelector()}

      {this.shouldShowInfo && (
        <div styleName='input-group'>
          <ContactPersonInfo contactPerson={this.state.selected} />
          {!this.props.externalControls && (
            <button onClick={this.update}>
              <FormattedMessage {...messages.editButtonLabel} />
            </button>
          )}
          {this.props.externalControls && this.props.children({
            currentContactPerson: this.state.selected,
            isInfo: true,
            update: this.update,
          })}
        </div>
      )}

      {this.shouldShowForm && (
        <ContactPersonForm
          externalControls={this.props.externalControls}
          cancelAdd={this.cancelAdd}
          cancelUpdate={this.cancelUpdate}
          isAdd={this.state.state === ContactPersonManager.STATE_ADD}
          isUpdate={this.state.state === ContactPersonManager.STATE_UPDATE}
          onAdd={this.onAdd}
          onUpdate={this.onUpdate}
          toUpdate={this.state.selected}
          working={this.props.addInProgress || this.props.updateInProgress}
        >
          {this.props.externalControls && (
            ({ submit, isValid }) => this.props.children({
              cancelAdd: this.cancelAdd,
              cancelUpdate: this.cancelUpdate,
              isAdd: this.state.state === ContactPersonManager.STATE_ADD,
              isUpdate: this.state.state === ContactPersonManager.STATE_UPDATE,
              isValid,
              onAdd: submit,
              onUpdate: submit,
              working: (
                this.props.addInProgress
                || this.props.updateInProgress
              ),
            })
          )}
        </ContactPersonForm>
      )}

      {(!this.shouldShowInfo
        && !this.shouldShowForm
        && this.props.externalControls) && this.props.children({
          currentContactPerson: this.state.selected,
        })}
    </div>;
}

ContactPersonManager.propTypes = propTypes;
ContactPersonManager.defaultProps = defaultProps;

function mapStateToProps(state) {
  return {
    actionLog: state.contactPersons.actionLog,
    addError: state.contactPersons.addError,
    addInProgress: state.contactPersons.addInProgress,
    company: getActiveCompany(state),
    contactPersons: getActiveCompanyContactPersons(state),
    latestAction: state.contactPersons.latestAction,
    updateError: state.contactPersons.updateError,
    updateInProgress: state.contactPersons.updateInProgress,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      addContactPerson,
      fetchContactPersons,
      updateContactPerson,
    },
    dispatch,
  );
}

export default connect(
  mapStateToProps, mapDispatchToProps
)(ContactPersonManager);
